<script setup lang="ts">
import { onMounted, ref } from "vue";
import { Bell, Send, Users } from "lucide-vue-next";
import { v3 } from "@/services/v3";
import { useSessionStore } from "@/stores/session";

defineProps<{ embedded?: boolean }>();
const session = useSessionStore();
const items = ref<any[]>([]);
const notifications = ref<any[]>([]);
const loading = ref(false);
const error = ref("");
const work = ref({ title: "", description: "", visibility: "team", sourceTaskId: "" });

async function load() {
  const [workResult, notificationResult] = await Promise.all([v3.workItems(), v3.notifications()]);
  items.value = workResult.items;
  notifications.value = notificationResult.notifications;
}
async function run(action: () => Promise<unknown>) {
  loading.value = true;
  error.value = "";
  try { await action(); await load(); }
  catch (cause: any) { error.value = cause?.message || "操作失败，请重试"; }
  finally { loading.value = false; }
}
async function addWork() {
  if (!work.value.title.trim()) return;
  await run(() => v3.createWorkItem({ ...work.value, sourceTaskId: Number(work.value.sourceTaskId) || null }));
  work.value = { title: "", description: "", visibility: "team", sourceTaskId: "" };
}

onMounted(() => void load());
</script>

<template>
  <div class="collab" :class="{ embedded }">
    <p v-if="error" class="error">{{ error }}</p>
    <div class="grid">
      <section>
        <header class="section-head"><div><h2><Users :size="18" />内部协同</h2><p>引用现有任务、提出优化方案，并通过 @账号 通知同事。</p></div></header>
        <div class="form"><input v-model="work.title" class="input" placeholder="协同标题" /><input v-model="work.sourceTaskId" class="input" inputmode="numeric" placeholder="引用任务 ID（可选）" /><textarea v-model="work.description" class="textarea" placeholder="优化方案或修改建议，可输入 @员工账号"></textarea><button class="btn btn--primary" :disabled="loading" @click="addWork"><Send :size="16" />创建协同</button></div>
        <article v-for="row in items" :key="row.id" class="work"><header><b>{{ row.title }}</b><span>{{ row.status }}</span></header><p>{{ row.description }}</p><small>{{ row.creator_name }}<template v-if="row.source_task_title"> · 引用：{{ row.source_task_title }}</template></small><button v-if="row.creator_id === session.user?.id && row.status === 'draft'" class="btn" :disabled="loading" @click="run(() => v3.submitWorkItem(row.id))">提交管理员审核</button></article>
        <p v-if="!items.length" class="empty">暂无内部协同事项。</p>
      </section>
      <aside><h2><Bell :size="18" />通知</h2><article v-for="row in notifications" :key="row.id" :class="{ unread: !row.read_at }" @click="run(() => v3.readNotification(row.id))"><b>{{ row.title }}</b><p>{{ row.content }}</p><small>{{ row.created_at.slice(0, 16).replace('T', ' ') }}</small></article><p v-if="!notifications.length" class="empty">暂无通知</p></aside>
    </div>
  </div>
</template>

<style scoped>
.collab{min-height:calc(100vh - 64px);padding:24px 28px;background:#f5f7f7}.collab.embedded{min-height:0;max-width:1480px;margin:0 auto;padding:18px 0;background:transparent}.grid{display:grid;grid-template-columns:minmax(0,1fr) 320px;gap:16px}.grid>section,.grid>aside{align-self:start;padding:20px;background:#fff;border:1px solid var(--line)}h2{display:flex;align-items:center;gap:7px;margin:0;font-size:17px}.section-head p{margin:5px 0 16px;color:var(--muted)}.form{display:grid;grid-template-columns:1fr 220px;gap:8px;margin-bottom:13px}.form .textarea{grid-column:1/-1;min-height:76px}.form .btn{display:flex;grid-column:1/-1;align-items:center;justify-content:center;gap:6px}.work{padding:14px 0;border-top:1px solid var(--line)}.work header{display:flex;justify-content:space-between;gap:10px}.work header span{color:var(--brand-dark);font-size:11px}.work p,.grid aside p{margin:5px 0;color:var(--muted);white-space:pre-wrap}.work small,.grid aside small{color:var(--muted)}.work .btn{margin-top:9px}.grid aside h2{margin-bottom:10px}.grid aside article{padding:11px 0;border-top:1px solid var(--line);cursor:pointer}.grid aside article.unread{border-left:3px solid var(--brand);padding-left:9px}.empty{padding:24px 0;color:var(--muted);text-align:center}.error{padding:9px 12px;border:1px solid #e9b7b0;background:#fff1ef;color:#9b3f36}
@media(max-width:850px){.grid{grid-template-columns:1fr}.form{grid-template-columns:1fr 180px}}
@media(max-width:700px){.collab,.collab.embedded{padding:10px 0 24px}.grid{gap:10px}.grid>section,.grid>aside{border-width:1px 0;padding:16px 13px}.form{grid-template-columns:1fr}.form .textarea,.form .btn{grid-column:auto}}
</style>
